import { datadogRum } from '@datadog/browser-rum';

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * @param {import('../config/config').HeliosEnvVars} config - Application configuration object
 */
export default function initDatadogRum(config) {
  // Check if required configuration is present
  if (!config.datadog_rum_client_token || !config.datadog_rum_application_id) {
    return;
  }

  // Helper function to redact sensitive data (JWT tokens)
  function redactSensitiveData(str) {
    if (!str) {
      return str;
    }
    return str.replace(
      /[a-zA-Z0-9_=]{36}\.[a-zA-Z0-9_=]{51}\.[a-zA-Z0-9_\-+/=]{43}/u,
      'JWT_REDACTED'
    );
  }

  try {
    datadogRum.init({
      applicationId: config.datadog_rum_application_id,
      clientToken: config.datadog_rum_client_token,
      site: config.datadog_rum_site,
      service: config.datadog_rum_service,
      env: config.server_env || 'development',
      version: config.nova_version,
      // Session sample rate (0.0 to 1.0)
      // 100% in development, configurable in production
      sessionSampleRate: config.datadog_rum_session_sample_rate ?? 100,
      // Session replay sample rate (0.0 to 1.0)
      // Set to 0 if you don't want session replay, or a lower value to reduce costs
      sessionReplaySampleRate: 0,
      // Track user interactions
      trackUserInteractions: true,
      // Track resources (images, scripts, etc.)
      trackResources: true,
      // Track long tasks
      trackLongTasks: true,
      // Default privacy level
      defaultPrivacyLevel: 'mask-user-input',
      // Before send callback to redact sensitive information
      beforeSend(event) {
        // Redact sensitive data from URLs
        if (event.view?.url) {
          event.view.url = redactSensitiveData(event.view.url);
        }

        // Redact sensitive data from resource URLs
        if (event.resource?.url) {
          event.resource.url = redactSensitiveData(event.resource.url);
        }

        // Redact sensitive data from action target URLs
        if (event.action?.target?.name) {
          event.action.target.name = redactSensitiveData(event.action.target.name);
        }

        // Redact sensitive data from error messages
        if (event.error?.message) {
          event.error.message = redactSensitiveData(event.error.message);
        }

        // Redact sensitive data from error stack
        if (event.error?.stack) {
          event.error.stack = redactSensitiveData(event.error.stack);
        }

        return event;
      }
    });
  } catch (error) {
    console.error('Error initializing Datadog RUM:', error);
  }
}

/**
 * Set user context for Datadog RUM
 * @param {object|null} user - User object with id, email, etc.
 */
export function setDatadogRumUser(user) {
  if (!user?.id) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: user.id
  });
}
